import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ProductCard from '../components/ProductCard';
import { affiliateService } from '../services/affiliateService';
import './Dashboard.css';

// Get Backend JWT auth token
const getAuthToken = async () => {
  try {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    // Get backend JWT token by calling google-login endpoint
    const loginResponse = await fetch(`/api/auth/google-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: user.email,
        googleId: user.uid
      })
    });

    if (loginResponse.ok) {
      const loginData = await loginResponse.json();
      return loginData.token;
    } else {
      console.error('Failed to get backend token');
      return null;
    }
  } catch (error) {
    console.error('Error getting backend token:', error);
    return null;
  }
};

export default function Dashboard() {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      fetchUserProducts();
    }
  }, [user]);

  useEffect(() => {
    // Filter products based on search query
    if (searchQuery.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.platform.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchQuery, products]);

  const fetchUserProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get JWT token for authentication
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Fetch products that this user has added from their affiliate links
      const response = await fetch(`/api/UserProducts/user/${user.uid}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        throw new Error(`Failed to fetch products: ${response.status}`);
      }
      
      const data = await response.json();
      setProducts(data || []);
      setFilteredProducts(data || []); // Initialize filtered products
    } catch (err) {
      console.error('Error fetching user products:', err);
      setError(`Failed to load your products: ${err.message}`);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-container">
          <h2>❌ Error</h2>
          <p>{error}</p>
          <button 
            onClick={fetchUserProducts}
            className="retry-btn"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard ${darkMode ? "dark" : ""}`}>
      <div className="dashboard-header">
        <h1>🛍️ Your Product Dashboard</h1>
        <p className="subtitle">
          Manage products you've added from your affiliate links
        </p>
        
        {/* Search Bar */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Search products by title, category, or platform..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`search-input ${darkMode ? "dark" : ""}`}
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="clear-search-btn"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="stats">
          <div className="stat-card">
            <h3>{filteredProducts.length}</h3>
            <p>{searchQuery ? 'Filtered Products' : 'Total Products'}</p>
          </div>
          <div className="stat-card">
            <h3>{filteredProducts.filter(p => p.category === 'Electronics').length}</h3>
            <p>Electronics</p>
          </div>
          <div className="stat-card">
            <h3>{filteredProducts.filter(p => p.category === 'Fashion').length}</h3>
            <p>Fashion</p>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {products.length === 0 ? (
          <div className="empty-dashboard">
            <h3>📦 No products yet</h3>
            <p>Add products from your affiliate links to see them here!</p>
            <a href="/account" className="add-products-btn">
              Add Affiliate Link
            </a>
          </div>
        ) : filteredProducts.length === 0 && searchQuery ? (
          <div className="empty-dashboard">
            <h3>🔍 No products found</h3>
            <p>No products match your search for "{searchQuery}"</p>
            <button 
              onClick={() => setSearchQuery('')}
              className="clear-search-btn-primary"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <>
            {searchQuery && (
              <div className="search-results-info">
                <p>Showing {filteredProducts.length} of {products.length} products for "{searchQuery}"</p>
              </div>
            )}
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  showAffiliateInfo={false}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="dashboard-footer">
        <p>
          Share your dashboard: <strong>{window.location.origin}/dashboard/{user?.uid}</strong>
        </p>
      </div>
    </div>
  );
}
